import api from 'lib/CfcLmsApi'
import blobApi from 'lib/blobApi'
import documentServiceApi from 'lib/DocumentServiceApi'
import { actions } from './reducer'
import { addNotification } from 'store/notifications'
import { getReadableErrorMessage } from 'utils/errorUtils'
import { createTrainingReportDownloadData } from './selector'

const fetchHistoricalReportDataFromBlobStorage = async ({
  url,
  name,
  fullBlob,
}) => {
  try {
    const { data } = url ? await blobApi.getBlobData(url) : { data: [] }
    return {
      fullBlob,
      name,
      data,
    }
  } catch (error) {
    console.error(
      'fetchHistoricalReportDataFromBlobStorage failed',
      { error },
      { url },
      { fullBlob },
    )
    return null
  }
}

export const fetchLessonProgressReport = (siteId, skuCode) => {
  return async dispatch => {
    dispatch(actions.fetchingReport(skuCode))
    try {
      const programCode = skuCode === 'K8P' ? 'K8' : 'HS'
      const response = await documentServiceApi.post('/SiteReport', {
        siteId,
        program: programCode,
        options: {
          useFlexTemplate: true,
          useHsFullTemplate: true,
        },
      })
      const sasUrl = response.data.sasUrl
      dispatch(actions.fetchingReportSuccess({ program: skuCode, reportData: sasUrl }))
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to get Lesson Progress Report',
      )
      dispatch(actions.fetchingReportError({ error, program: skuCode }))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
    }
  }
}

export const deleteReports = () => {
  return async dispatch => {
    dispatch(actions.deleteReportsSuccess())
  }
}

export const deleteLessonProgressReport = () => {
  return async dispatch => {
    dispatch(actions.deleteReportsSuccess())
  }
}

export const fetchHistoricalReports = (siteId: number) => {
  return async dispatch => {
    dispatch(actions.fetchingHistoricalReportData())
    try {
      const [k8Json, k8Excel] = await Promise.all([
        api.getHistoricalSiteReports(siteId),
        api.getK8SiteHistoricReports(siteId),
      ])
      const k8JsonReports = Array.isArray(k8Json?.data) ? k8Json.data : []
      const k8JsonPromises = k8JsonReports.map(report =>
        fetchHistoricalReportDataFromBlobStorage(report),
      )
      const historicalK8JsonReportData = (
        await Promise.all(k8JsonPromises)
      ).filter(Boolean)
      const historicalReportData = historicalK8JsonReportData.concat(
        k8Excel?.data || [],
      )
      dispatch(
        actions.fetchingHistoricalReportDataSuccess({
          historicalReportData,
          siteId,
        }),
      )
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to get historical K-8 reports',
      )
      dispatch(actions.fetchingHistoricalReportDataError({ error: errorMessage }))
      dispatch(addNotification({ color: 'danger', text: errorMessage }))
    }
  }
}

export const fetchHsHistoricalReports = (siteId: number) => {
  return async dispatch => {
    dispatch(actions.fetchingHsHistoricalReportData())
    try {
      const hsExcel = await api.getHsSiteHistoricReports(siteId)
      dispatch(
        actions.fetchingHsHistoricalReportDataSuccess({
          hsHistoricalReportData: hsExcel?.data || [],
          siteId,
        }),
      )
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to get historical High School reports',
      )
      dispatch(
        actions.fetchingHsHistoricalReportDataError({ error: errorMessage }),
      )
      dispatch(addNotification({ color: 'danger', text: errorMessage }))
    }
  }
}

export const fetchTrainingReport = (learners, program, siteName) => {
  return async dispatch => {
    dispatch(actions.fetchingReport(program))
    try {
      const { data: trainingReportData } = await api.getTrainingReport(
        learners,
        program.toLowerCase(),
      )
      const formattedTrainingReportData = createTrainingReportDownloadData(
        trainingReportData,
        program,
        siteName,
      )
      dispatch(
        actions.fetchingReportSuccess({
          program,
          reportData: formattedTrainingReportData,
        }),
      )
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to get training report',
      )
      dispatch(actions.fetchingReportError({ error, program }))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
    }
  }
}

export default {
  deleteReports,
  deleteLessonProgressReport,
  fetchHistoricalReports,
  fetchHsHistoricalReports,
  fetchLessonProgressReport,
  fetchTrainingReport,
}
